import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { playPop } from "@/lib/sound";
import { Fx, Chat } from "@/pages/Comunicacion";
import DocenteMessages from "@/components/app/DocenteMessages";

// Ventana de chat flotante. Para docentes incluye la bandeja "Alumnos y padres".
export default function ChatWidget() {
  const { user } = useAuth();
  const isDocente = user?.role === "docente";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("team");
  const [chatStudent, setChatStudent] = useState(null);
  const [unread, setUnread] = useState(0);
  const [stUnread, setStUnread] = useState(0);
  const alive = useRef(true);
  const prevUnread = useRef(null);

  // Abrir conversación de un alumno desde un aviso (evento) o desde ?chat=ID en la URL.
  useEffect(() => {
    if (!isDocente) return;
    const openStudent = (sid) => { setChatStudent(sid); setView("students"); setOpen(true); };
    const sid = new URLSearchParams(window.location.search).get("chat");
    if (sid) { openStudent(sid); window.history.replaceState(null, "", window.location.pathname); }
    const h = (e) => openStudent(e.detail);
    window.addEventListener("uai-open-student-chat", h);
    return () => window.removeEventListener("uai-open-student-chat", h);
  }, [isDocente]);

  useEffect(() => {
    alive.current = true;
    const load = async () => {
      try {
        const { data } = await api.get("/chat/unread");
        let s = 0;
        if (isDocente) {
          const r = await api.get("/docente/student-messages").catch(() => null);
          s = (r?.data?.threads || []).reduce((a, t) => a + (t.unread || 0), 0);
        }
        if (!alive.current) return;
        const n = (data.unread || 0) + s;
        if (prevUnread.current !== null && n > prevUnread.current && !open) {
          try { playPop(); } catch {}
          try { navigator.vibrate?.(60); } catch {}
        }
        prevUnread.current = n;
        setUnread(data.unread || 0);
        setStUnread(s);
      } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive.current = false; clearInterval(t); };
  }, [open, isDocente]);

  const total = unread + stUnread;
  useEffect(() => { window.dispatchEvent(new CustomEvent("zv-unread", { detail: { key: "chat", n: total } })); }, [total]);
  useEffect(() => {
    const r = document.documentElement;
    if (open) { r.removeAttribute("data-fab-open"); r.setAttribute("data-chat-open", ""); } else r.removeAttribute("data-chat-open");
    return () => r.removeAttribute("data-chat-open");
  }, [open]);
  const tabCls = (on) => `flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${on ? "bg-white text-navy" : "text-white/80 hover:bg-white/15"}`;

  return (
    <div className={`fixed right-4 z-[70] flex flex-col items-end ${open ? "bottom-2 md:bottom-4" : "bottom-[180px] md:bottom-[86px]"}`} data-testid="chat-widget">
      <Fx />
      {open && (
        <div className="mb-[-56px] w-[92vw] max-w-[380px] uai-fade-up relative z-[1]" data-testid="chat-widget-panel">
          <div className="px-3 py-2 rounded-t-2xl bg-gradient-to-r from-navy-light to-navy text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold"><MessageCircle className="w-4 h-4" /> {isDocente ? "Mis chats" : "Chat del equipo"}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setOpen(false)} data-testid="chat-widget-min" title="Minimizar" className="p-1.5 hover:bg-white/15 rounded-lg"><Minus className="w-4 h-4" /></button>
                <button onClick={() => setOpen(false)} data-testid="chat-widget-close" title="Cerrar" className="p-1.5 hover:bg-white/15 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>
            {isDocente && (
              <div className="flex gap-1 mt-2 bg-white/10 p-1 rounded-lg">
                <button onClick={() => setView("team")} data-testid="chat-tab-team" className={tabCls(view === "team")}>Equipo{unread > 0 ? ` (${unread})` : ""}</button>
                <button onClick={() => { setChatStudent(null); setView("students"); }} data-testid="chat-tab-students" className={tabCls(view === "students")}>Alumnos y padres{stUnread > 0 ? ` (${stUnread})` : ""}</button>
              </div>
            )}
          </div>
          <div className="h-[68vh] max-h-[560px] [&_[data-testid=chat-panel]]:rounded-t-none [&_[data-testid=chat-panel]]:h-full">
            {view === "students" && isDocente ? (
              <div className="h-full overflow-y-auto bg-nardo-50 p-2 rounded-b-2xl shadow-lg" data-testid="chat-students-panel">
                <DocenteMessages key={chatStudent || "list"} initialStudentId={chatStudent} compact />
              </div>
            ) : (
              <Chat heightClass="h-full" compact />
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-toggle"
        className="zv-fab-item relative w-14 h-14 rounded-full bg-gradient-to-br from-navy-light to-navy text-white shadow-2xl shadow-navy/40 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        title={isDocente ? "Mis chats" : "Chat del equipo"}
      >
        <MessageCircle className="w-6 h-6" />
        {total > 0 && !open && (
          <span data-testid="chat-widget-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">{total > 99 ? "99+" : total}</span>
        )}
      </button>
    </div>
  );
}
